import MapboxDraw from '@mapbox/mapbox-gl-draw'
import type { Feature } from 'geojson'
import {
  LucideCircleDotDashed,
  LucideMousePointerSquareDashed,
  LucideTrash2,
  LucideWaypoints,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useMap } from 'react-map-gl'
import { aDrawMode, aDrawnFeature, useAtom } from '~/atoms'
import { ControlTooltip } from '~/components/common'
import { Button } from '~/components/ui'
import { cn } from '~/lib'
import { MAP_DRAW_STYLES } from './draw/drawStyles'
import {
  CircleMode,
  DirectModeOverride,
  SimpleSelectModeOverride,
} from './draw/modes'

type MapboxDrawEvent = {
  features: Feature[]
}

export default function DrawControl() {
  const mapRef = useMap()
  const drawRef = useRef<MapboxDraw>()
  const [isOpen, setIsOpen] = useState(false)
  const [drawMode, setDrawMode] = useAtom(aDrawMode)
  const [drawnFeature, setDrawnFeature] = useAtom(aDrawnFeature)
  const [_, setSelectedFeature] = useState<{} | null>(null)

  const onUpdate = useCallback((e: MapboxDrawEvent) => {
    if (e.features.length > 0 && e.features[0]) {
      setDrawnFeature(e.features[0])
    }
  }, [])

  const deleteFeature = useCallback(() => {
    setDrawnFeature(null)
    setSelectedFeature(null)
    drawRef.current!.trash()
    drawRef.current!.deleteAll()
  }, [])

  useEffect(() => {
    if (!drawnFeature && drawRef.current) {
      deleteFeature()
    }
  }, [drawnFeature, drawRef.current])

  useEffect(() => {
    if (!mapRef.current || drawRef.current) {
      return
    }

    drawRef.current = new MapboxDraw({
      displayControlsDefault: false,
      styles: MAP_DRAW_STYLES,
      userProperties: true,
      modes: {
        ...MapboxDraw.modes,
        draw_circle: CircleMode,
        direct_select: DirectModeOverride,
        simple_select: SimpleSelectModeOverride,
      },
    })
    mapRef.current!.addControl(drawRef.current! as any)
    mapRef.current!.on('draw.create', (e) => {
      onUpdate(e)
    })
    mapRef.current!.on('draw.update', (e) => {
      onUpdate(e)
    })
    mapRef.current!.on('draw.selectionchange', (e: MapboxDrawEvent) => {
      if (e.features.length > 0) {
        setSelectedFeature(e.features[0])
        setDrawMode('simple_select')
      } else {
        setSelectedFeature(null)
        setDrawMode(null)
      }
    })

    if (drawnFeature) {
      drawRef.current!.add(drawnFeature)
      setIsOpen(true)
    }
  }, [mapRef.current])

  return (
    <div
      className={cn(
        'hidden items-center gap-2 rounded-md shadow-md lg:flex',
        // isOpen && 'bg-muted/65 pr-2'
        isOpen &&
          'bg-background/65 pr-2 backdrop-blur supports-[backdrop-filter]:bg-background/65'
      )}>
      <ControlTooltip text="Start Drawing" side="bottom">
        <Button
          variant="map"
          size="map"
          className="w-auto px-3 shadow-none"
          onClick={() => {
            if (isOpen) {
              setDrawMode(null)
              setTimeout(() => {
                drawRef.current!.changeMode('simple_select')
              }, 200)

              if (!drawnFeature) {
                setIsOpen(false)
              }
            } else {
              setIsOpen(true)
            }
          }}>
          <LucideMousePointerSquareDashed className="mr-2 h-5 w-5" />
          Draw
        </Button>
      </ControlTooltip>
      {isOpen && (
        <>
          <ControlTooltip text="Draw Polygon" side="bottom">
            <Button
              variant="map"
              size="map"
              className={cn(
                'h-7 w-7 shadow-none',
                drawMode === 'draw_polygon' && 'bg-primary text-white'
              )}
              onClick={() => {
                deleteFeature()
                setDrawMode('draw_polygon')
                setTimeout(() => {
                  drawRef.current!.changeMode('draw_polygon')
                }, 200)
              }}>
              <LucideWaypoints className="h-4 w-4" />
            </Button>
          </ControlTooltip>
          <ControlTooltip text="Draw Circle" side="bottom">
            <Button
              variant="map"
              size="map"
              className={cn(
                'relative h-7 w-7 shadow-none',
                drawMode === 'draw_circle' && 'bg-primary text-white'
              )}
              onClick={() => {
                deleteFeature()
                setDrawMode('draw_circle')
                setTimeout(() => {
                  drawRef.current!.changeMode('draw_circle')
                }, 200)
              }}>
              <LucideCircleDotDashed className="h-4 w-4" />
            </Button>
          </ControlTooltip>
          {drawnFeature && (
            <ControlTooltip text="Delete Drawing" side="bottom">
              <Button
                variant="map"
                size="map"
                className="h-7 w-7 shadow-none"
                onClick={() => {
                  deleteFeature()
                  setDrawMode(null)
                }}>
                <LucideTrash2 className="h-4 w-4" />
              </Button>
            </ControlTooltip>
          )}
        </>
      )}
    </div>
  )
}
